//import moment from 'moment';
import moment from "moment-timezone";
import { takeEvery, put, call, select } from "redux-saga/effects";
import { defineMessages } from "react-intl";
import * as BillingActions from "./billingActions";
import * as TaskActions from "./../task/taskActions";
import * as AttachmentsActions from "./../attachments/attachmentsActions";
import { makeApiCall, getTechnicianInfo } from "../helpers";
import taskServices from "./../../services/taskServices";
import { ALERT_TYPES, showAlert } from "./../alert/alertActions";

const messages = defineMessages({
    fetchFailed: {
        id: "alerts.billing-sagas.fetch-failed",
        defaultMessage: "Unable to retrieve billing activities: {responseError}",
        description: "Fetch billing activities failed",
    },
    saveSucceeded: {
        id: "alerts.billing-sagas.save-succeeded",
        defaultMessage: "Save billing SKUs succeeded",
        description: "Save billing activities succeeded alert message",
    },
    saveFailed: {
        id: "alerts.billing-sagas.save-failed",
        defaultMessage: "Save billing SKUs failed: {responseError}",
        description: "Save billing activities failed alert message",
    },
    saveLocatesSucceeded: {
        id: "alerts.related-work-sagas.save-succeeded",
        defaultMessage: "Save related work Locates succeeded",
        description: "Save related work Locates succeeded alert message",
    },
    saveLocatesFailed: {
        id: "alerts.related-work-sagas.save-failed",
        defaultMessage: "Save related work Locates failed: {responseError}",
        description: "Save related work Locates failed alert message",
    },
    saveContractorCommentSuccess: {
        id: "alerts.related-work-sagas.save-comment-success",
        defaultMessage: "Saved contractor comment successfully",
        description: "Saved contractor comment successfully alert message",
    },
    saveContractorCommentFailed: {
        id: "alerts.related-work-sagas.save-comment-failed",
        defaultMessage: "Save contractor comment Locates failed: {responseError}",
        description: "Save contractor comment failed alert message",
    },
});

export const watchBillingActivitiesRequests = function* () {
    yield takeEvery(BillingActions.REQUEST_BILLING_ACTIVITIES, fetchBillingActivities);

    yield takeEvery(BillingActions.SAVE_BILLING_SKU_INFO, saveBillingInfo);

    yield takeEvery(BillingActions.SAVE_LOCATE_INFO, saveLocateInfo);

    yield takeEvery(BillingActions.REQUEST_BILLING_COMMENTS, fetchBillingComments);

    yield takeEvery(BillingActions.UPDATE_CONTRACTOR_COMMENT, updateContractorComment);

    yield takeEvery(BillingActions.ADD_CONTRACTOR_COMMENT, addContractorComment);
};

export const fetchBillingActivities = function* (action) {
    try {
        let billingActivities = yield call(makeApiCall, taskServices.getBillingActivities, {
            contractNumber: action.billingActivitiesRequest.contractNumber,
            completionDate: moment(action.completedDate).format("YYYY-MM-DD"),
        }) || [];
        if (!billingActivities || billingActivities.statusCode != "OK") {
            billingActivities = [];
        } else {
            billingActivities = billingActivities.body;
        }

        if (billingActivities.length > 0) {
            billingActivities.forEach((activity) => {
                activity.quantity = 0;
                activity.selected = false;
                return activity;
            });

            // Returned activities already include a quantity before we set it
            yield put(BillingActions.requestBillingActivitiesSucceeded(billingActivities));
        }
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.fetchFailed, ALERT_TYPES.error, { responseError: responseError.body }));
        yield put(BillingActions.requestBillingActivitiesFailed());
    }
};

export const saveBillingInfo = function* (action) {
    try {
        const technicianInfo = yield select(getTechnicianInfo);
        const completionDate = action.completionDate ? action.completionDate : new Date();

        // Upload files first
        if (action.filesToBeUploaded && action.filesToBeUploaded.length > 0)
            yield put(AttachmentsActions.uploadAttachments(action.filesToBeUploaded));

        const billingResponse = yield call(makeApiCall, taskServices.saveBillingActivities, {
            ...action.billingSKUInfoRequest,
            workOrderNumber: action.workOrderNumber,
            userName: technicianInfo.userName,
            completionDate: moment(completionDate).format("YYYY-MM-DD"),
        });
        yield put(BillingActions.saveBillingSKUInfoSucceeded(billingResponse));
        // refresh the updated task
        if (action?.refresh) {
            yield put(TaskActions.fetchWorkorder(action.workOrderNumber, true));
        }
        if ((billingResponse.statusCode = 202)) {
            yield put(showAlert(Date.now(), messages.saveSucceeded, ALERT_TYPES.success));
        } else {
            yield put(showAlert(Date.now(), messages.saveFailed, ALERT_TYPES.error));
        }
        yield put(TaskActions.disableBellToSendButton(false));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.saveFailed, ALERT_TYPES.error, { responseError: responseError.body }));
        yield put(BillingActions.saveBillingSKUInfoSucceeded({ failed: true }));
        yield put(TaskActions.disableBellToSendButton(false));
    }
};

//saveLocateInfo
export const saveLocateInfo = function* (action) {
    try {
        const technicianInfo = yield select(getTechnicianInfo);
        const billingResponse = yield call(
            makeApiCall,
            taskServices.saveRelatedWorkLocates,
            {
                ...action.relatedWorkRequest,
                workOrderNumber: action.workOrderNumber,
            },
            technicianInfo.userName,
        );

        yield put(BillingActions.saveRelatedWorkInfoSucceeded(billingResponse));

        // refresh the updated task
        if (action.refresh) {
            yield put(TaskActions.fetchWorkorder(action.workOrderNumber, true));
        }
        yield put(showAlert(Date.now(), messages.saveLocatesSucceeded, ALERT_TYPES.success));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.saveLocatesFailed, ALERT_TYPES.error, { responseError: responseError.body }));
        yield put(BillingActions.saveRelatedWorkInfoSucceeded({ failed: true }));
    }
};

// fetchBillingComments
export const fetchBillingComments = function* (action) {
    try {
        let billingComments = yield call(makeApiCall, taskServices.getBillingComments, action.workOrderNumber) || [];
        // maping time zone to local
        billingComments = billingComments.map((comment) => {
            let newCreatedtDate = comment.createdtDate ? new Date(comment.createdtDate) : "";
            let newUpdatedDate = comment.updatedDate ? new Date(comment.updatedDate) : "";
            comment.createdtDate = comment.createdtDate ? new Date(newCreatedtDate.getTime() + newCreatedtDate.getTimezoneOffset() * 60000) : "";
            comment.createdDate = comment.createdDate ? moment.tz(comment.createdDate, moment.tz.guess()) : "";
            comment.updatedDate = comment.updatedDate ? new Date(newUpdatedDate.getTime() + newUpdatedDate.getTimezoneOffset() * 60000) : "";
            return comment;
        });
        const comment = billingComments?.filter((comment) => comment?.incompletionComment).map((comment) => comment)[0] ?? "";
        if (comment?.createdBy) {
            let userNameDetails = yield call(makeApiCall, taskServices.getUserNameDetail, comment?.createdBy) || [];
            comment.firstName = userNameDetails[0].firstName;
            comment.lastName = userNameDetails[0].lastName;
        }
        yield put(BillingActions.requestBillingCommentsSucceeded(billingComments));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.fetchFailed, ALERT_TYPES.error, { responseError: responseError.body }));
        yield put(BillingActions.requestBillingCommentsFailed());
    }
};

// updateContractorComment
const updateContractorComment = function* (action) {
    const { obj = {}, newComment } = action;
    const technicianInfo = yield select(getTechnicianInfo);

    try {
        yield makeApiCall(taskServices.commentPatch, obj.id, {
            comment: newComment,
            updatedBy: technicianInfo.userName,
            updatedDate: moment.utc(new Date()).format("YYYY-MM-DD HH:mm:00"),
        });

        // refresh the updated comments
        yield put(BillingActions.fetchBillingComments(obj.workOrderId));

        yield put(showAlert(Date.now(), messages.saveContractorCommentSuccess, ALERT_TYPES.success));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.saveContractorCommentFailed, ALERT_TYPES.error, { responseError: responseError.body }));
    }
};

const addContractorComment = function* (action) {
    const { newComment } = action;
    const technicianInfo = yield select(getTechnicianInfo);
    newComment.createdDate = moment.utc(new Date()).format("YYYY-MM-DD HH:mm:00");
    newComment.createdBy = technicianInfo.userName;
    try {
        const patch = yield makeApiCall(taskServices.addContractorComment, newComment);

        // refresh the updated comments
        yield put(BillingActions.fetchBillingComments(newComment.workOrderId));

        yield put(showAlert(Date.now(), messages.saveContractorCommentSuccess, ALERT_TYPES.success));
    } catch (responseError) {
        yield put(showAlert(Date.now(), messages.saveContractorCommentFailed, ALERT_TYPES.error, { responseError: responseError.body }));
    }
};
